import { Modal } from 'components/Modal/Modal';
import { WizardStateBlock } from 'components/StepperWizard/WizardStateBlock';
import { useState } from 'react';
import LocationInfoForm from './LocationInfoForm';
import InvoiceDetailsForm from './InvoiceDetailsForm';

import { useMutation } from 'react-query';
import { postInvoiceRebill } from 'api/orders/api';
import showToast from 'components/Toast/Toast';
import { SiteAddress } from 'api/addresssearch/schema';
import { OptionType } from 'components/Select/Select';
import { RebillInvoiceDetails, RebillLocationsInfo } from './schemas';

interface RebillServiceDetailsModalProps {
  isOpen: boolean;
  close: () => void;
  invoiceId?: string;
  cwLocations?:
    | (SiteAddress & { account_name: string; site_name: string })[]
    | null;
  location: {
    address1: string;
    address2?: string | null;
    city: string;
    state: string;
    zip_code: string;
    id?: string;
  } | null;
  onConfirm?: () => void;
}

const RebillServiceDetailsModal = ({
  isOpen,
  close,
  invoiceId,
  cwLocations,
  location,
  onConfirm,
}: RebillServiceDetailsModalProps) => {
  const [step, setStep] = useState<number>(1);
  const [selectedOption, setSelectedOption] = useState<OptionType | null>(null);
  const [formKey, setFormKey] = useState<number>(0);

  const [locationInfo, setLocationInfo] = useState<RebillLocationsInfo | null>(
    null,
  );

  const [invoiceDetails, setInvoiceDetails] =
    useState<RebillInvoiceDetails | null>(null);
  const [showCreateAccountFields, setShowCreateAccountFields] = useState(false);

  const resetForms = () => {
    setFormKey((prev) => prev + 1);
    setLocationInfo(null);
    setInvoiceDetails(null);
    setStep(1);
    setShowCreateAccountFields(false);
    setSelectedOption(null);
  };

  const { mutate, isLoading } = useMutation(
    (data: RebillLocationsInfo & RebillInvoiceDetails) => {
      if (!invoiceId) throw new Error('Invoice ID is required');
      return postInvoiceRebill(invoiceId, data);
    },
    {
      onSuccess: () => {
        showToast.confirmation({ message: 'Rebill process was initiated' });
        onConfirm?.();

        resetForms();
        close();
      },
    },
  );

  const handleLocationInfoSubmit = (data: RebillLocationsInfo): void => {
    setLocationInfo(data);
    setStep(2);
  };

  const handleInvoiceDetailsBack = (draft: RebillInvoiceDetails) => {
    setInvoiceDetails(draft);
    setStep(1);
  };

  const handleInvoiceDetailsSubmit = (data: RebillInvoiceDetails): void => {
    if (!locationInfo) return;
    mutate({ ...locationInfo, ...data });
  };

  const handleClose = () => {
    resetForms();
    close();
  };

  return (
    <Modal
      isVisible={isOpen}
      close={handleClose}
      className="!max-h-full !w-[1012px] justify-between sm:!max-h-[90vh]"
      enableScrolling
    >
      <div className="flex flex-col rounded-lg bg-background-base-surface-2">
        <div className="bg-background-base-surface-3 px-8 py-6 font-bold leading-9">
          <h1 className="text-xl text-content-base-default lg:text-2xl">
            Provide invoice details
          </h1>
          <div className="mt-1 grid grid-cols-2 items-end gap-1">
            <WizardStateBlock
              label="Location info"
              isActive={step >= 1}
              isCompleted={step > 1}
            />
            <WizardStateBlock
              label="Invoice details"
              isActive={step >= 2}
              isCompleted={step > 2}
            />
          </div>
        </div>

        {step === 1 && (
          <LocationInfoForm
            key={formKey}
            cwLocations={cwLocations ?? []}
            location={location}
            onNext={handleLocationInfoSubmit}
            onCancel={close}
            initialData={locationInfo || undefined}
            showCreateAccountFields={showCreateAccountFields}
            setShowCreateAccountFields={setShowCreateAccountFields}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
          />
        )}

        {step === 2 && (
          <InvoiceDetailsForm
            key={formKey}
            initialData={invoiceDetails || undefined}
            onBack={handleInvoiceDetailsBack}
            onSubmit={handleInvoiceDetailsSubmit}
            isLoading={isLoading}
            location={location}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            cwLocations={cwLocations ?? []}
          />
        )}
      </div>
    </Modal>
  );
};

export default RebillServiceDetailsModal;
